import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { showToast } from "src/utils/showToaster";
import { useUploadExcelMutation } from "src/services/ProductService";
import UploadExcelForm from "../Layouts/UploadExcel";
import * as XLSX from "xlsx";

type Props = {
  onClose: () => void;
};

const UploadExcelWrapper = ({ onClose }: Props) => {
  const initialValues = {
    file: null as File | null,
  };

  const [uploadExcel] = useUploadExcelMutation();

  // State for preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);

  const handleSubmit = async (
    values: typeof initialValues,
    { setSubmitting, resetForm }: FormikHelpers<typeof initialValues>
  ) => {
    if (!values.file) {
      showToast("error", "Please select a file before uploading");
      setSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", values.file);

    try {
      const res: any = await uploadExcel(formData);

      if (res?.error) {
        showToast("error", res?.error?.data?.message || "Upload failed");
      } else if (res?.data?.status) {
        showToast(
          "success",
          res?.data?.message || "File uploaded successfully ✅"
        );
        resetForm();
        onClose();
      } else {
        showToast("error", res?.data?.message || "Upload failed");
      }
    } catch (err) {
      console.error(err);
      showToast("error", "Something went wrong while uploading");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Excel Preview
  const handlePreview = (file?: File | null) => {
    if (!file) {
      showToast("error", "Please upload a file first");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (!data) return;

      const workbook = XLSX.read(data, { type: "binary" });
      const firstSheet = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheet];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      setPreviewData(jsonData.slice(0, 20)); // show first 20 rows
      setPreviewOpen(true);
    };

    reader.readAsBinaryString(file);
  };

  return (
    <>
      <Dialog open maxWidth="xs" fullWidth onClose={onClose}>
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {(formikProps) => (
            <Form>
              <UploadExcelForm
                formikProps={formikProps}
                onClose={onClose}
                onPreview={() => handlePreview(formikProps.values.file)}
              />
            </Form>
          )}
        </Formik>
      </Dialog>
      {/* Excel Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <div className="bg-white rounded-lg shadow-lg max-h-[80vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center border-b px-5 py-3 bg-gray-50 sticky top-0 z-10">
            <h2 className="text-lg font-semibold text-gray-800">
              Excel Preview
            </h2>
            <button
              type="button"
              className="text-gray-600 hover:text-gray-800 transition text-sm font-medium"
              onClick={() => setPreviewOpen(false)}
            >
              ✕ Close
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-4">
            {previewData.length > 0 ? (
              <div className="overflow-auto border rounded-lg">
                <table className="min-w-full border-collapse text-sm">
                  <thead className="bg-gray-100 sticky top-0 z-10">
                    <tr>
                      {(previewData[0] as any[])?.map((headerCell, index) => (
                        <th
                          key={index}
                          className="px-4 py-2 text-left font-semibold text-gray-700 border-b border-r whitespace-nowrap"
                        >
                          {headerCell || `Column ${index + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(1).map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={`${
                          rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-gray-100 transition`}
                      >
                        {(row as any[]).map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="px-4 py-2 border-b border-r text-gray-800 align-top break-words"
                          >
                            {cell ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-10">
                No data to preview
              </p>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default UploadExcelWrapper;
